// Staff roster (nursing-ops sprint, Epic 7 / E7-FE; redesigned 2026-07-27 into
// a 4-tab shell): Weekly schedule, Roster entries, Leave requests, Nurse
// directory. All roster/nurse data is fetched once here and passed down —
// tabs are pure display, no tab owns its own fetch of shared data.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Dialog, DialogContent, DialogTitle, IconButton, Tab, Tabs, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";

import {
  getRoster, getWardRoster, updateWardRosterStatus, removeWardRoster, listOf,
  getRosterHours, copyRosterWeek,
} from "../api/nurseApi";
import useBedCensusData from "../../ipd/hooks/useBedCensusData";
import RosterAuthoringForm from "../components/RosterAuthoringForm";
import WeekGrid from "../components/WeekGrid";
import LeaveApprovalsSection from "../components/LeaveApprovalsSection";
import RosterEntriesTab from "../components/RosterEntriesTab";
import NurseDirectoryTab from "../components/NurseDirectoryTab";
import ShiftScheduleTab from "../components/ShiftScheduleTab";

const TABS = [
  { key: "weekly", label: "Weekly schedule" },
  { key: "entries", label: "Roster entries" },
  { key: "leave", label: "Leave requests" },
  { key: "directory", label: "Nurse directory" },
  { key: "shifts", label: "Shift schedule" },
];

export default function StaffRoster() {
  const { beds } = useBedCensusData();
  // A ward is a room category. This used to be a department -> ward map built by
  // grouping beds by their department; beds no longer have one, and a shared ICU
  // is a single ward rather than one per department.
  const wards = useMemo(() => {
    const seen = new Map();
    beds.forEach((b) => {
      if (!b.room_category_code || seen.has(b.room_category_code)) return;
      seen.set(b.room_category_code, b.room_category_name || b.room_category_code);
    });
    return Array.from(seen, ([code, name]) => ({ code, name }));
  }, [beds]);
  const wardNameByCode = useMemo(() => {
    const m = {};
    beds.forEach((b) => {
      if (b.room_category_code && !(b.room_category_code in m)) m[b.room_category_code] = b.room_category_name || b.room_category_code;
    });
    return m;
  }, [beds]);

  const [tab, setTab] = useState("weekly");
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);

  const [nurses, setNurses] = useState([]);
  const [nursesLoading, setNursesLoading] = useState(true);
  // Resolves a roster/leave row's nurse_id to a name when the row itself
  // didn't carry one — never let the raw hex id leak into the UI.
  const nurseNameById = useMemo(() => {
    const m = {};
    nurses.forEach((n) => { m[n.nurse_id] = n.name; });
    return m;
  }, [nurses]);
  const [rosterRows, setRosterRows] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [hoursByNurse, setHoursByNurse] = useState({});
  const [copying, setCopying] = useState(false);

  const reloadNurses = useCallback(() => {
    setNursesLoading(true);
    getRoster().then((r) => setNurses(listOf(r))).catch(() => {}).finally(() => setNursesLoading(false));
  }, []);

  const reloadHours = useCallback(() => {
    getRosterHours()
      .then((r) => {
        const map = {};
        listOf(r).forEach((h) => { map[h.nurse_id] = h; });
        setHoursByNurse(map);
      })
      .catch(() => setHoursByNurse({}));
  }, []);

  const reloadRoster = useCallback(() => {
    setRosterLoading(true);
    reloadHours();
    return getWardRoster()
      .then((r) => setRosterRows(listOf(r)))
      .catch(() => setRosterRows([]))
      .finally(() => setRosterLoading(false));
  }, [reloadHours]);

  useEffect(() => { reloadNurses(); reloadRoster(); }, [reloadNurses, reloadRoster]);

  const handleCopyWeek = async () => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    const src = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, "0")}-${String(monday.getDate()).padStart(2, "0")}`;
    setCopying(true);
    try {
      const res = await copyRosterWeek(src);
      const created = Array.isArray(res?.data) ? res.data.length : 0;
      const skipped = (res?.skipped || []).length;
      const errors = res?.errors || [];
      if (res?.status !== "Success" && created === 0) {
        toast.error(res?.message || "Copy week failed");
      } else if (errors.length > 0) {
        toast.error(`${created} copied, ${skipped} skipped, ${errors.length} rejected — first: ${errors[0]?.nurse ? `${errors[0].nurse} ` : ""}${errors[0]?.roster_date}: ${errors[0]?.error}`);
      } else {
        toast.success(res?.message || `${created} row(s) copied, ${skipped} skipped`);
      }
      await reloadRoster();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Copy week failed");
    } finally {
      setCopying(false);
    }
  };

  const handleStatusChange = async (row, newStatus) => {
    if (newStatus === row.status) return;
    setSavingId(row.id);
    try {
      const res = await updateWardRosterStatus(row.id, newStatus);
      if (res?.status !== "Success") {
        toast.error(res?.message || "Status update failed");
        return;
      }
      toast.success(res?.message || "Roster status updated");
      await reloadRoster();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Status update failed");
    } finally {
      setSavingId(null);
    }
  };

  const handleRemove = async (row) => {
    if (!window.confirm(`Remove ${row.nurse_name || nurseNameById[row.nurse_id] || row.nurse_id}'s roster row for ${row.roster_date}?`)) return;
    setSavingId(row.id);
    try {
      const res = await removeWardRoster(row.id);
      if (res?.status !== "Success") {
        toast.error(res?.message || "Remove failed");
        return;
      }
      toast.success(res?.message || "Roster row removed");
      await reloadRoster();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Remove failed");
    } finally {
      setSavingId(null);
    }
  };

  const sortedRows = useMemo(
    () => [...rosterRows].sort((a, b) => String(b.roster_date).localeCompare(String(a.roster_date))),
    [rosterRows]
  );

  const closeBulkAssign = () => setBulkAssignOpen(false);
  const handleBulkCreated = () => {
    reloadRoster();
    setBulkAssignOpen(false);
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">Staff Roster</Typography>
        <Typography variant="body2" color="text.secondary">Build shift schedules, review leave requests, and see who's on duty this week.</Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, next) => setTab(next)}
        sx={{
          mb: 2, minHeight: 0,
          "& .MuiTabs-flexContainer": { gap: 0.5 },
          "& .MuiTab-root": { minHeight: 36, borderRadius: 2, textTransform: "none", fontWeight: 600 },
        }}
      >
        {TABS.map((t) => (<Tab key={t.key} value={t.key} label={t.label} />))}
      </Tabs>

      <Box sx={{ display: tab === "weekly" ? "block" : "none" }}>
        <WeekGrid
          wards={wards} nurses={nurses} onChanged={reloadRoster}
          onBulkAssign={() => setBulkAssignOpen(true)} onCopyWeek={handleCopyWeek} copying={copying}
        />

        <Dialog open={bulkAssignOpen} onClose={closeBulkAssign} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            Bulk assign shifts
            <IconButton size="small" onClick={closeBulkAssign}><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <DialogContent>
            <RosterAuthoringForm wards={wards} nurses={nurses} onCreated={handleBulkCreated} />
          </DialogContent>
        </Dialog>
      </Box>

      <Box sx={{ display: tab === "entries" ? "block" : "none" }}>
        <RosterEntriesTab
          rows={sortedRows}
          hoursByNurse={hoursByNurse}
          wardNameByCode={wardNameByCode}
          nurseNameById={nurseNameById}
          savingId={savingId}
          loading={rosterLoading}
          onStatusChange={handleStatusChange}
          onRemove={handleRemove}
        />
      </Box>

      <Box sx={{ display: tab === "leave" ? "block" : "none" }}>
        <LeaveApprovalsSection onChanged={reloadRoster} beds={beds} nurseNameById={nurseNameById} />
      </Box>

      <Box sx={{ display: tab === "directory" ? "block" : "none" }}>
        <NurseDirectoryTab nurses={nurses} loading={nursesLoading} />
      </Box>

      <Box sx={{ display: tab === "shifts" ? "block" : "none" }}>
        <ShiftScheduleTab onChanged={reloadRoster} />
      </Box>
    </Box>
  );
}
